<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6 overflow-hidden">
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 shrink-0 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Department Inventory</h2>
        <button
          type="button"
          @click="openIngredientStatusModal"
          class="w-full md:w-auto md:min-w-[200px] px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer text-[#1e293b] font-semibold hover:border-[#0d4169]"
        >
          Ingredient Status
        </button>
      </div>

      <!-- Filters -->
      <form class="px-6 pb-5 pt-2 shrink-0" @submit.prevent>
        <div class="flex flex-wrap items-center gap-x-12 gap-y-4">
          <div class="flex items-center gap-3 min-w-[240px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Calendar:</label>
            <input
              v-model="currentDate"
              type="date"
              @keydown.prevent
              @change="loadInventory"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
          </div>
          <div class="flex items-center gap-3 min-w-[280px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Department:</label>
            <select
              v-model="selectedDepartment"
              @change="loadInventory"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Department</option>
              <option v-for="dept in filteredDepartments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
            </select>
          </div>
        </div>
      </form>

      <!-- Date label -->
      <!-- <div class="px-6 pb-1">
        <p class="text-[0.78rem] font-semibold text-slate-500">Date: <span class="text-[#0d4169]">{{ currentDate || 'Not selected' }}</span></p>
      </div> -->

      <!-- Table -->
      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <template v-else>
        <table class="w-full daily-inventory-table">
          <thead>
            <tr>
              <th class="w-10 text-center sticky-col bg-[#f1f5f9] no-side-border">No.</th>
              <th class="min-w-[35px] sticky-col left-[40px] bg-[#f1f5f9] no-side-border">Ingredient Name</th>
              <th class="w-24">U.O.M st</th>
              <th class="w-24">U.O.M pro(f.b)</th>
              <th class="w-28">Qty Pack</th>
              <th class="w-36">Volume</th>
              <th class="w-32">Converted</th>
              <th class="w-28">Qty Single</th>
              <th class="w-32">Total Qty</th>
              <th class="w-28 text-right px-4">Unit Price</th>
              <th class="w-28 text-right px-4">T.Price</th>
              <th class="w-32">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in visibleInventory" :key="`inv-${row.type}-${row.id ?? index}`" :class="['hover:bg-slate-50 transition-colors group', row.type === 'product' ? 'bg-emerald-50/50' : '']">
              <td class="text-center sticky-col group-hover:bg-slate-50 no-side-border" :class="row.type === 'product' ? 'bg-emerald-50/50' : 'bg-white'">{{ index + 1 }}</td>
              <td class="sticky-col left-[40px] group-hover:bg-slate-50 no-side-border font-medium" :class="row.type === 'product' ? 'bg-emerald-50/50 text-emerald-800' : 'bg-white'">
                {{ row.ingredientName || row.name }}
              </td>
              <td>{{ row.uomStore || row.uomFB }}</td>
              <td>{{ getRowUomFBDisplay(row) }}</td>
              <td class="w-28">
                <input v-model.number="row.qtyPack" type="number" 
                  @input="calcTotalForRow(row)"
                  :disabled="inventoryFieldDisabled(row, 'qtyPack')"
                  :class="inventoryFieldDisabled(row, 'qtyPack') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class=" w-16  px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm">
              </td>
              <td class="w-36">
                <input v-model.number="row.volume" type="number"
                  @input="calcTotalForRow(row)"
                  :disabled="inventoryFieldDisabled(row, 'volume')"
                  :class="volumeInputClass(row)"
                  class="w-16 px-1 py-0.5 border rounded outline-none transition-all text-sm">
              </td>
              <td>{{ formatQuantity(row.converted) }}</td>
              <td class="w-28">
                <input v-model.number="row.qtySingle" type="number" 
                  @input="calcTotalForRow(row)"
                  :disabled="inventoryFieldDisabled(row, 'qtySingle')"
                  :class="inventoryFieldDisabled(row, 'qtySingle') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class=" w-16 px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm">
              </td>
              <td>{{ formatQuantity(row.totalQty) }}</td>
              <td class="text-right px-4">{{ money2(row.unitPrice) }}</td>
              <td class="font-black text-[#0d4169] text-right px-4 bg-slate-50/30">{{ money2(row.tPrice) }}</td>
              <td>
                <input v-model="row.remark" type="text"
                  :disabled="inventoryFieldDisabled(row, 'remark')"
                  :class="inventoryFieldDisabled(row, 'remark') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm"
                  placeholder="...">
              </td>
            </tr>
            <tr v-if="visibleInventory.length > 0" class="bg-slate-50 border-t-2 border-slate-200">
              <td colspan="10"></td>
              <td class="px-4 text-right text-slate-900 font-extrabold whitespace-nowrap">
                <span class="text-slate-700 font-bold mr-2">Total:</span>{{ money2(tPriceColumnSum) }}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <p v-if="volumeValidationError" class="mt-3 text-center text-xs text-red-600 font-semibold">
          {{ volumeValidationError }}
        </p>
        <div class="flex justify-center mt-6">
          <button
            @click="openSaveModal"
            :disabled="!canSave || loading"
            :class="(!canSave || loading) ? 'opacity-60 cursor-not-allowed' : ''"
            class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md"
          >
            Save
          </button>
        </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Ingredient Status modal -->
  <div v-if="ingredientStatusModal.open" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeIngredientStatusModal">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">Ingredient Status</h3>
        </div>
        <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeIngredientStatusModal" aria-label="Close ingredient status modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-5 overflow-auto max-h-[70vh] custom-scrollbar">
        <table class="w-full daily-inventory-table">
          <thead>
            <tr>
              <th class="w-10 text-center">No.</th>
              <th class="min-w-[120px]">Ingredient Name</th>
              <th class="w-24">U.O.M st</th>
              <th class="w-24">U.O.M pro(f.b)</th>
              <th class="w-28 text-right px-4">Unit Price</th>
              <th class="w-20 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in inventory"
              :key="`status-${row.type}-${row.id ?? index}`"
              :class="['hover:bg-slate-50 transition-colors', row.type === 'product' ? 'bg-emerald-50/50' : '']"
            >
              <td class="text-center">{{ index + 1 }}</td>
              <td class="font-medium" :class="row.type === 'product' ? 'text-emerald-800' : ''">
                {{ row.ingredientName || row.name }}
              </td>
              <td>{{ row.uomStore || row.uomFB }}</td>
              <td>{{ getRowUomFBDisplay(row) }}</td>
              <td class="text-right px-4">{{ money2(row.unitPrice) }}</td>
              <td class="text-center">
                <label
                  class="toggle-switch"
                  :class="row.__togglingTempo ? 'opacity-50 pointer-events-none' : ''"
                  title="Toggle ingredient tempo status"
                >
                  <input
                    type="checkbox"
                    :checked="isTempoOn(row)"
                    @change="toggleIngredientTempoStatus(row)"
                  >
                  <span class="slider"></span>
                </label>
              </td>
            </tr>
            <tr v-if="inventory.length === 0">
              <td colspan="6" class="text-center text-slate-500 py-8">No ingredients in the current list.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="px-5 py-4 flex items-center justify-end gap-2 border-t border-slate-100">
        <button type="button" @click="closeIngredientStatusModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Close</button>
      </div>
    </div>
  </div>

  <!-- Save confirmation modal -->
  <div v-if="saveModal.open" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeSaveModal">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">Confirm Save Inventory</h3>
        </div>
        <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeSaveModal" aria-label="Close save modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-5 space-y-4">
        <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>

        <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
          {{ saveModal.code }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
          <input
            v-model.trim="saveModal.input"
            type="text"
            maxlength="4"
            class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
          >
        </div>
        <div v-if="saveModal.error" class="text-xs text-red-600 font-semibold">{{ saveModal.error }}</div>
      </div>

      <div class="px-5 py-4 flex items-center justify-end gap-2">
        <button type="button" @click="closeSaveModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
        <button
          type="button"
          @click="confirmSave"
          :disabled="saving"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
        >
          {{ saving ? 'Saving...' : 'Confirm' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { apiFetch } from '../../../utils/api'
import store from '../../../store'

const selectedDepartment = ref('')
const currentDate = ref('')
const todayDate = ref(new Date().toISOString().split('T')[0])
const departments = ref([])
const inventory = ref([])
const loading = ref(false)
const isSnapshotLocked = ref(false)
const saving = ref(false)

function sortByIngredientName(rows) {
  const arr = Array.isArray(rows) ? rows : []
  return arr.sort((a, b) =>
    String(a?.ingredientName || a?.name || '').localeCompare(String(b?.ingredientName || b?.name || ''), undefined, { sensitivity: 'base' })
  )
}

function applyPendingEditPermissions(rows, pendingList) {
  const list = Array.isArray(pendingList) ? pendingList : [];
  /** Flags from GET /daily-inventory → pendingEditAccess (daily_inventory_edit_access), camelCase booleans. */
  const flagOn = (p, key) => {
    const v = p[key];
    return v === true || v === 1;
  };
  const matchesInventoryRow = (p, dailyInventoryId) =>
    dailyInventoryId != null &&
    Number.isFinite(Number(dailyInventoryId)) &&
    Number(p.dailyInventoryId) === Number(dailyInventoryId);
  return rows.map((row) => {
    const rid = row.dailyInventoryId;
    const relevant = list.filter((p) => matchesInventoryRow(p, rid));
    return {
      ...row,
      canEditQtyPack: relevant.some((p) => flagOn(p, 'qtyPack')),
      canEditVolume: relevant.some((p) => flagOn(p, 'volume')),
      canEditQtySingle: relevant.some((p) => flagOn(p, 'qtySingle')),
      canEditRemark: relevant.some((p) => flagOn(p, 'remark')),
    };
  });
}

function normalizeUomText(uom) {
  return String(uom || '').toLowerCase().replace(/\./g, '').trim()
}

function isLiterOrKgStoreUom(row) {
  const st = normalizeUomText(row?.uomStore || row?.uomST || row?.uom_store || row?.uom || '')
  return st === 'liter' || st === 'kg' || st === 'kilogram'
}

function shouldLockVolumeAt1000(row) {
  // Apply only for non-saved snapshot rows.
  return !isSnapshotLocked.value && isLiterOrKgStoreUom(row)
}

function enforceVolumeRuleForRow(row) {
  if (!row) return row
  if (shouldLockVolumeAt1000(row)) {
    row.volume = 1000
  } else if (!Number.isFinite(Number(row?.volume))) {
    row.volume = 0
  }
  return row
}

function inventoryFieldDisabled(row, field) {
  if (isFutureDateSelected()) return true;

  if (row?.type === 'product') {
    if (field === 'qtyPack' || field === 'volume') return true;
  }

  if (field === 'qtyPack' || field === 'volume') {
    const st = normalizeUomText(row?.uomStore || row?.uomST || row?.uom_store || row?.uom || '')
    const fb = normalizeUomText(row?.uomFB || row?.uomPro || row?.uom_fb || '')
    if (st && fb && st === fb) return true
  }
  if (field === 'volume' && shouldLockVolumeAt1000(row)) return true

  if (isSnapshotLocked.value) {
    if (field === 'qtyPack') return !row.canEditQtyPack;
    if (field === 'volume') return !row.canEditVolume;
    if (field === 'qtySingle') return !row.canEditQtySingle;
    if (field === 'remark') return !row.canEditRemark;
    return true;
  }
  return false;
}

function isFutureDateSelected() {
  if (!currentDate.value) return false
  return currentDate.value > todayDate.value
}

function hasMeaningfulRowData(row) {
  const qtyPack = Number(row?.qtyPack || 0)
  const qtySingle = Number(row?.qtySingle || 0)
  const volume = Number(row?.volume || 0)
  const remark = String(row?.remark ?? '').trim()
  return qtyPack !== 0 || qtySingle !== 0 || volume !== 0 || remark.length > 0
}

function hasEnteredQtyPack(row) {
  const qtyPack = Number(row?.qtyPack)
  return Number.isFinite(qtyPack) && qtyPack !== 0
}

function isVolumeFieldEditable(row) {
  return !inventoryFieldDisabled(row, 'volume')
}

/** Qty Pack entered + editable Volume => Volume must be > 0 */
function isVolumeRequiredMissing(row) {
  if (!hasEnteredQtyPack(row)) return false
  if (!isVolumeFieldEditable(row)) return false
  const volume = Number(row?.volume)
  return !(Number.isFinite(volume) && volume > 0)
}

function hasVolumeValidationErrors() {
  return visibleInventory.value.some(isVolumeRequiredMissing)
}

function volumeInputClass(row) {
  if (inventoryFieldDisabled(row, 'volume')) {
    return 'bg-slate-100 opacity-60 cursor-not-allowed border-transparent'
  }
  if (isVolumeRequiredMissing(row)) {
    return 'border-red-400 bg-red-50 focus:border-red-500 focus:bg-white'
  }
  return 'border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] bg-red-50 focus:bg-white'
}

function isTempoOn(row) {
  return Number(row?.tempoStatus ?? 1) === 1
}

function normalizeRowTempoStatus(row) {
  if (!row) return row
  const tempoStatus = row.tempoStatus ?? 1
  row.tempoStatus = Number(tempoStatus) === 1 ? 1 : 0
  return row
}

const visibleInventory = computed(() =>
  inventory.value.filter((row) => isTempoOn(row))
)

const volumeValidationError = computed(() => {
  if (!hasVolumeValidationErrors()) return ''
  return 'Volume is required for all rows where Qty Pack is entered and Volume is editable.'
})

const canSave = computed(() => {
  if (saving.value) return false;
  if (!selectedDepartment.value || !currentDate.value) return false;
  if (isFutureDateSelected()) return false;
  if (visibleInventory.value.length === 0) return false;
  if (!visibleInventory.value.some(hasMeaningfulRowData)) return false;
  if (hasVolumeValidationErrors()) return false;
  if (isSnapshotLocked.value) {
    const hasPermission = visibleInventory.value.some(row => row.canEditQtyPack || row.canEditVolume || row.canEditQtySingle || row.canEditRemark);
    if (!hasPermission) return false;
  }
  return true;
});

const tPriceColumnSum = computed(() =>
  visibleInventory.value.reduce((sum, row) => sum + Number(row?.tPrice || 0), 0)
)

const saveModal = ref({
  open: false,
  code: '',
  input: '',
  error: ''
})

const ingredientStatusModal = ref({
  open: false
})

function openIngredientStatusModal() {
  if (!selectedDepartment.value || !currentDate.value) {
    alert('Please select both Calendar date and Department first.')
    return
  }
  if (loading.value) {
    alert('Please wait for the inventory list to finish loading.')
    return
  }
  ingredientStatusModal.value.open = true
}

function closeIngredientStatusModal() {
  ingredientStatusModal.value.open = false
}

async function toggleIngredientTempoStatus(row) {
  if (!row || row.__togglingTempo) return

  const departmentId = Number(selectedDepartment.value)
  const entityId = Number(row.type === 'product'
    ? row.productId ?? row.id
    : row.itemId ?? row.id)
  if (!Number.isInteger(departmentId) || departmentId <= 0
    || !Number.isInteger(entityId) || entityId <= 0) return

  const nextStatus = isTempoOn(row) ? 0 : 1
  const prevStatus = isTempoOn(row) ? 1 : 0

  row.__togglingTempo = true
  row.tempoStatus = nextStatus

  try {
    const res = await apiFetch('/daily-inventory/tempo-status', {
      method: 'PATCH',
      body: JSON.stringify({
        departmentId,
        itemId: row.type === 'product' ? null : entityId,
        productId: row.type === 'product' ? entityId : null,
        tempoStatus: nextStatus,
      }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to update ingredient status')
    }
    const data = await res.json().catch(() => ({}))
    row.tempoStatus = Number(data.tempoStatus ?? nextStatus) === 1 ? 1 : 0
  } catch (err) {
    row.tempoStatus = prevStatus
    alert(err?.message || 'Failed to update ingredient status')
  } finally {
    row.__togglingTempo = false
  }
}

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveModal() {
  if (hasVolumeValidationErrors()) {
    alert(volumeValidationError.value || 'Volume is required where Qty Pack is entered.')
    return
  }
  if (!canSave.value) return
  saveModal.value.open = true
  saveModal.value.code = generateVerificationCode()
  saveModal.value.input = ''
  saveModal.value.error = ''
}

function closeSaveModal() {
  if (saving.value) return
  saveModal.value.open = false
}

async function confirmSave() {
  const expected = String(saveModal.value.code || '').trim()
  const entered = String(saveModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveModal.value.error = ''
  const ok = await saveAllRows()
  if (ok) {
    saveModal.value.open = false
  }
}

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

// All departments from token as names list (e.g. "Kitchen, Bar")
const userDeptList = computed(() => {
  const depts = currentUser.value?.departments || currentUser.value?.department?.name || currentUser.value?.departmentName || currentUser.value?.dept
  if (!depts) return []
  if (typeof depts === 'string') return depts.split(',').map(d => d.trim()).filter(Boolean)
  if (Array.isArray(depts)) return depts.filter(Boolean)
  return [depts].filter(Boolean)
})

const filteredDepartments = computed(() => {
  if (!userDeptList.value.length) return departments.value
  return departments.value.filter(d =>
    userDeptList.value.some(userDept => userDept.toLowerCase() === String(d.name || '').toLowerCase())
  )
})

watch(filteredDepartments, (newVal) => {
  if (newVal.length === 1 && !selectedDepartment.value) {
    selectedDepartment.value = newVal[0].id
    setTimeout(() => { loadInventory() }, 50)
  }
}, { deep: true })

async function fetchDepartments() {
  try {
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')

    const url = query ? `/departments?${query}` : '/departments'

    const res = await apiFetch(url)
    if (!res.ok) return
    const data = await res.json().catch(() => [])

    const raw = Array.isArray(data) ? data : []
    // Keep only active departments; userDeptList filter happens in filteredDepartments
    departments.value = raw.filter(d => {
      if (Number(d.status ?? 1) !== 1) return false
      const isCentral = d.isCentral ?? d.is_central
      return isCentral === 0 || isCentral === false
    })
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error fetching departments:', err)
  }
}

async function loadInventory() {
  if (!selectedDepartment.value || !currentDate.value) return
  loading.value = true
  try {
    // Future dates should always show only the base item/product list,
    // never saved snapshot data, and always as read-only with zero values.
    if (isFutureDateSelected()) {
      isSnapshotLocked.value = true
      const ingredientsRes = await apiFetch(`/daily-inventory/ingredients/${selectedDepartment.value}?date=${todayDate.value}`)
      if (ingredientsRes.ok) {
        const data = await ingredientsRes.json()
        inventory.value = data.map(row => {
          applySinglePriceFromRecent(row)
          enforceVolumeRuleForRow(row)
          normalizeRowTempoStatus(row)
          row.qtyPack = 0
          row.qtySingle = 0
          row.tPrice = 0
          row.remark = ''
          return row
        })
        sortByIngredientName(inventory.value)
      } else {
        inventory.value = []
      }
      return
    }

    // If rows already saved for this date+department (+company/branch by auth),
    // load only from daily_inventories and lock editing.
    const savedRes = await apiFetch(`/daily-inventory?departmentId=${selectedDepartment.value}&calendar=${currentDate.value}`)
    if (savedRes.ok) {
      const savedPayload = await savedRes.json().catch(() => ({ hasData: false, rows: [] }))
      let savedRows = [];
      let pendingEditAccess = [];
      
      if (Array.isArray(savedPayload)) {
          savedRows = savedPayload;
      } else if (savedPayload && savedPayload.rows) {
          savedRows = savedPayload.rows;
          pendingEditAccess = savedPayload.pendingEditAccess || [];
      }

      if (Array.isArray(savedRows) && savedRows.length > 0) {
        isSnapshotLocked.value = true
        let processedRows = savedRows
          .map(row => ({
            ...row,
            dailyInventoryId: row.id,
            id: row.type === 'item' ? row.itemId : row.productId,
            name: row.ingredientName,
            converted: Number(row?.converted ?? 0),
            totalQty: Number(row?.totalQty ?? 0),
            unitPrice: Number(row?.unitPrice ?? 0),
            tPrice: Number(row?.totalPrice ?? row?.tPrice ?? 0),
            volume: Number(row?.volume ?? 0),
            tempoStatus: row.tempoStatus ?? 1,
          }))
          .map((row) => normalizeRowTempoStatus(row))
        sortByIngredientName(processedRows)
        
        inventory.value = applyPendingEditPermissions(processedRows, pendingEditAccess)
        return
      }
    }

    // If no saved data and date is not today (past date), show base list
    // like today's no-saved flow and keep it editable.
    if (currentDate.value !== todayDate.value) {
      isSnapshotLocked.value = false
      const ingredientsRes = await apiFetch(`/daily-inventory/ingredients/${selectedDepartment.value}?date=${currentDate.value}`)
      if (ingredientsRes.ok) {
        const data = await ingredientsRes.json()
        inventory.value = data.map(row => {
          applySinglePriceFromRecent(row)
          enforceVolumeRuleForRow(row)
          normalizeRowTempoStatus(row)
          if (row.type === 'product') {
            if (Number(row.uPricePack || 0) === 0) row.qtyPack = 0
            if (Number(row.uPriceSingle || 0) === 0) row.qtySingle = 0
          }
          if (row.type !== 'product' && !isMetric(row.uomFB)) {
            row.qtySingle = 0
          }
          row.converted = Number(row?.converted ?? 0)
          row.totalQty = Number(row?.totalQty ?? 0)
          row.unitPrice = Number(row?.unitPrice ?? 0)
          row.tPrice = Number(row?.tPrice ?? 0)
          return row
        })
        await fillMissingProductUnitPrices()
        inventory.value = inventory.value.map((row) => {
          applySinglePriceFromRecent(row)
          if (row.type === 'product') {
            if (Number(row.uPricePack || 0) === 0) row.qtyPack = 0
            if (Number(row.uPriceSingle || 0) === 0) row.qtySingle = 0
          }
          return row
        })
        inventory.value.forEach((_, i) => calcTotal(i))
        sortByIngredientName(inventory.value)
      } else {
        inventory.value = []
      }
      return
    }

    isSnapshotLocked.value = false
    const ingredientsRes = await apiFetch(`/daily-inventory/ingredients/${selectedDepartment.value}?date=${currentDate.value}`)
    if (ingredientsRes.ok) {
      const data = await ingredientsRes.json()
      inventory.value = data.map(row => {
        applySinglePriceFromRecent(row)
        enforceVolumeRuleForRow(row)
        normalizeRowTempoStatus(row)
        if (row.type === 'product') {
          if (Number(row.uPricePack || 0) === 0) row.qtyPack = 0
          if (Number(row.uPriceSingle || 0) === 0) row.qtySingle = 0
        }
        if (row.type !== 'product' && !isMetric(row.uomFB)) {
          row.qtySingle = 0
        }
        row.converted = Number(row?.converted ?? 0)
        row.totalQty = Number(row?.totalQty ?? 0)
        row.unitPrice = Number(row?.unitPrice ?? 0)
        row.tPrice = Number(row?.tPrice ?? 0)
        return row
      })
      await fillMissingProductUnitPrices()
      inventory.value = inventory.value.map((row) => {
        applySinglePriceFromRecent(row)
        if (row.type === 'product') {
          if (Number(row.uPricePack || 0) === 0) row.qtyPack = 0
          if (Number(row.uPriceSingle || 0) === 0) row.qtySingle = 0
        }
        return row
      })
      inventory.value.forEach((_, i) => calcTotal(i))
      sortByIngredientName(inventory.value)
    }
  } catch (err) {
    console.error('Error loading inventory:', err)
  } finally {
    loading.value = false
  }
}

async function fillMissingProductUnitPrices() {
  const rows = Array.isArray(inventory.value) ? inventory.value : []
  const targets = rows.filter((r) => String(r?.type || '').toLowerCase() === 'product' && Number(r?.uPriceSingle || 0) === 0)
  if (targets.length === 0) return

  const productIds = [...new Set(
    targets
      .map((row) => Number(row?.id ?? row?.productId ?? row?.product_id ?? 0))
      .filter((pid) => Number.isFinite(pid) && pid > 0)
  )]
  if (!productIds.length) return

  try {
    const res = await apiFetch('/daily-inventory/product-unit-costs', {
      method: 'POST',
      body: JSON.stringify({ productIds })
    })
    if (!res.ok) return
    const payload = await res.json().catch(() => null)
    const costs = payload?.costs && typeof payload.costs === 'object' ? payload.costs : {}

    for (const row of targets) {
      const pid = Number(row?.id ?? row?.productId ?? row?.product_id ?? 0)
      const cpu = Number(costs[pid] ?? costs[String(pid)] ?? 0)
      if (cpu > 0) row.uPriceSingle = cpu
    }
  } catch (err) {
    console.error('Error filling missing product unit prices:', err)
  }
}

function calcTotal(index) {
  const row = inventory.value[index]
  calcTotalForRow(row)
}

function calcTotalForRow(row) {
  if (!row) return
  enforceVolumeRuleForRow(row)
  const uomFBDisplay = getRowUomFBDisplay(row)
  row.converted = Number(getRowConverted(row, uomFBDisplay) || 0)
  row.totalQty = Number(getRowTotalQty(row, uomFBDisplay) || 0)
  row.unitPrice = Number(getRowUPriceSingle(row, uomFBDisplay) || 0)
  row.tPrice = Number(row.totalQty || 0) * Number(row.unitPrice || 0)
}

function getRowUomFBDisplay(row) {
  if (String(row?.type || '').toLowerCase() !== 'product') return row?.uomFB
  const normalized = normalizeUomText(row?.uomFB)
  if (normalized === 'kg') return 'Gram'
  if (normalized === 'liter') return 'M.L.'
  return row?.uomFB
}

function getRowUPriceSingle(row, uomFBDisplay = null) {
  if (uomFBDisplay === 'Gram' || uomFBDisplay === 'M.L.') {
    return Number(row?.uPriceSingle || 0) * 1000
  }
  return Number(row?.uPriceSingle || 0)
}

function getRowVolume(row) {
  const vol = Number(row?.volume ?? 0)
  return Number.isFinite(vol) && vol > 0 ? vol : 0
}

function getRowConverted(row, uomFBDisplay = null) {
  if (uomFBDisplay === 'Glass') {
    return Number(row?.qtyPack || 0) * getRowVolume(row) / row?.glass_amount
  }
  return Number(row?.qtyPack || 0) * getRowVolume(row)
}

function getRowTotalQty(row, uomFBDisplay = null) {
  if (uomFBDisplay === 'Gram' || uomFBDisplay === 'M.L.') {
    return (getRowConverted(row) + Number(row?.qtySingle || 0)) / 1000
  }

 else if (uomFBDisplay === 'Glass' ) {
    return (getRowConverted(row) ) / row?.glass_amount+ Number(row?.qtySingle || 0)
  }
  return getRowConverted(row) + Number(row?.qtySingle || 0)
}

function formatQuantity(val) {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 5 })
}

function applySinglePriceFromRecent(row) {
  if (!row) return row

  const displayedUom = normalizeUomText(getRowUomFBDisplay(row))
  const isTargetUom = ['ml', 'milliliter', 'gram', 'g', 'gm', 'bottle', 'bottles', 'btl', 'pieces', 'piece', 'pc', 'pcs', 'glass', 'glasses'].includes(displayedUom)
  if (!isTargetUom) return row

  const recentUnitPrice = Number(row.recent_unit_price ?? row.recentUnitPrice ?? 0)
  const recentVolume = Number(row.recent_volume ?? row.recentVolume ?? 0)
  if (!(Number.isFinite(recentUnitPrice) && Number.isFinite(recentVolume) && recentVolume > 0)) {
    // Keep existing uPriceSingle to avoid clobbering when recent fields are absent.
    return row
  }

  let nextSingle = recentUnitPrice / recentVolume
  if (displayedUom === 'glass' || displayedUom === 'glasses') {
    const glassAmount = Number(row.glass_amount ?? row.glassAmount ?? 0)
    if (!(Number.isFinite(glassAmount) && glassAmount > 0)) return row
    nextSingle = nextSingle * glassAmount
  }

  row.uPriceSingle = nextSingle
  return row
}

function isMetric(uom) {
  if (!uom) return false
  const normalized = uom.toLowerCase().replace(/\./g, '').trim()
  return ['kg', 'liter', 'gram', 'ml', 'g', 'gm'].includes(normalized)
}

async function saveAllRows() {
  if (hasVolumeValidationErrors()) {
    alert(volumeValidationError.value || 'Volume is required where Qty Pack is entered.')
    return false
  }
  if (!canSave.value) return false
  saving.value = true
  try {
    const itemsToSave = visibleInventory.value.map((row) => ({
      ...row,
      qtyPack: Number(row?.qtyPack || 0),
      volume: Number(row?.volume ?? 0),
      converted: Number(row?.converted ?? 0),
      qtySingle: Number(row?.qtySingle || 0),
      totalQty: Number(row?.totalQty ?? 0),
      // Persist exactly what is shown in Unit Price column.
      uPriceSingle: Number(row?.unitPrice ?? row?.uPriceSingle ?? 0),
      unitPrice: Number(row?.unitPrice ?? 0),
      tPrice: Number(row?.tPrice ?? 0),
    }))
    if (itemsToSave.length === 0) {
      alert('No inventory rows to save.')
      return false
    }

    const res = await apiFetch('/daily-inventory/batch', {
      method: 'POST',
      body: JSON.stringify({
        date: currentDate.value,
        calendar: currentDate.value,
        departmentId: selectedDepartment.value,
        items: itemsToSave
      })
    })
    if (res.ok) {
      alert('All rows saved successfully!')
      selectedDepartment.value = ''
      inventory.value = []
      return true
    } else {
      const err = await res.json()
      alert('Error saving: ' + (err.message || 'Unknown error'))
      return false
    }
  } catch (err) {
    console.error('Error saving rows:', err)
    alert('Failed to save rows')
    return false
  } finally {
    saving.value = false
  }
}

function money2(val) {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

onMounted(() => {

  currentDate.value = ""
  fetchDepartments()
})
</script>

<style scoped>
.daily-inventory-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background-clip: padding-box;
  border-right: 2px solid #e2e8f0 !important;
}

.no-side-border {
  border-right: none !important;
  border-left: none !important;
}

th.sticky-col {
  z-index: 30;
}

.custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
.custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}
</style>
